import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./store/authStore";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieConsent from "./components/CookieConsent";
import CoachWidget from "./components/CoachWidget";
import FeedbackWidget from "./components/FeedbackWidget";
import MobileTabBar from "./components/MobileTabBar";
import SkipToContent from "./components/SkipToContent";

// Critical pages — loaded immediately
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Lazy-loaded pages — loaded on demand
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CollegeSearchPage = lazy(() => import("./pages/CollegeSearchPage"));
const CollegeDetailPage = lazy(() => import("./pages/CollegeDetailPage"));
const CollegeListPage = lazy(() => import("./pages/CollegeListPage"));
const DeadlinesPage = lazy(() => import("./pages/DeadlinesPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const EssaysListPage = lazy(() => import("./pages/EssaysListPage"));
const EssayCoachPage = lazy(() => import("./pages/EssayCoachPage"));
const ProgressPage = lazy(() => import("./pages/ProgressPage"));
const ParentDashboardPage = lazy(() => import("./pages/ParentDashboardPage"));
const ScholarshipsPage = lazy(() => import("./pages/ScholarshipsPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ExtracurricularsPage = lazy(() => import("./pages/ExtracurricularsPage"));
const FinaidComparePage = lazy(() => import("./pages/FinaidComparePage"));
const InterviewPrepPage = lazy(() => import("./pages/InterviewPrepPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const FafsaGuidePage = lazy(() => import("./pages/FafsaGuidePage"));
const TestStrategyPage = lazy(() => import("./pages/TestStrategyPage"));
const DecisionsPage = lazy(() => import("./pages/DecisionsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ActivityTrackerPage = lazy(() => import("./pages/ActivityTrackerPage"));
const AdmissionsScoringPage = lazy(() => import("./pages/AdmissionsScoringPage"));
const EarlyStrategyPage = lazy(() => import("./pages/EarlyStrategyPage"));
const MockInterviewPage = lazy(() => import("./pages/MockInterviewPage"));
const SocialMediaAuditPage = lazy(() => import("./pages/SocialMediaAuditPage"));
const RecLetterGuidePage = lazy(() => import("./pages/RecLetterGuidePage"));
const ApplicationReviewPage = lazy(() => import("./pages/ApplicationReviewPage"));
const PassionProjectPage = lazy(() => import("./pages/PassionProjectPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const CounselorBotPage = lazy(() => import("./pages/CounselorBotPage"));
const DiscoveryAssessmentPage = lazy(() => import("./pages/DiscoveryAssessmentPage"));
const WhatWeOfferPage = lazy(() => import("./pages/WhatWeOfferPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AnalyticsDashboardPage = lazy(() => import("./pages/AnalyticsDashboardPage"));
const VisaGuidancePage = lazy(() => import("./pages/VisaGuidancePage"));
const PendingConsentPage = lazy(() => import("./pages/PendingConsentPage"));
const ParentsPage = lazy(() => import("./pages/ParentsPage"));
const ParentalConsentConfirmedPage = lazy(() => import("./pages/ParentalConsentConfirmedPage"));
const VerifyEmailRequiredPage = lazy(() => import("./pages/VerifyEmailRequiredPage"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-slate-500">Loading...</div>
    </div>
  );
}

function CoachWidgetWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <CoachWidget />;
}

function SmartHome() {
  const { user } = useAuth();
  if (!user) return <LandingPage />;
  if (user.role === "PARENT") return <Navigate to="/parent" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { fetchMe } = useAuth();

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  return (
    <BrowserRouter>
      <SkipToContent />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<SmartHome />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pending-consent" element={<PendingConsentPage />} />
          <Route path="/verify-email-required" element={<VerifyEmailRequiredPage />} />
          <Route path="/parents" element={<ParentsPage />} />
          <Route path="/parental-consent-confirmed" element={<ParentalConsentConfirmedPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/roadmap" element={<ProtectedRoute><RoadmapPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/colleges" element={<ProtectedRoute><CollegeSearchPage /></ProtectedRoute>} />
          <Route path="/colleges/list" element={<ProtectedRoute><CollegeListPage /></ProtectedRoute>} />
          <Route path="/colleges/:scorecardId" element={<ProtectedRoute><CollegeDetailPage /></ProtectedRoute>} />
          <Route path="/deadlines" element={<ProtectedRoute><DeadlinesPage /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/essays" element={<ProtectedRoute><EssaysListPage /></ProtectedRoute>} />
          <Route path="/essays/:essayId" element={<ProtectedRoute><EssayCoachPage /></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
          <Route path="/parent" element={<ProtectedRoute><ParentDashboardPage /></ProtectedRoute>} />
          <Route path="/scholarships" element={<ProtectedRoute><ScholarshipsPage /></ProtectedRoute>} />
          <Route path="/what-we-offer" element={<WhatWeOfferPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/extracurriculars" element={<ProtectedRoute><ExtracurricularsPage /></ProtectedRoute>} />
          <Route path="/finaid" element={<ProtectedRoute><FinaidComparePage /></ProtectedRoute>} />
          <Route path="/interview-prep" element={<ProtectedRoute><InterviewPrepPage /></ProtectedRoute>} />
          <Route path="/fafsa-guide" element={<ProtectedRoute><FafsaGuidePage /></ProtectedRoute>} />
          <Route path="/test-strategy" element={<ProtectedRoute><TestStrategyPage /></ProtectedRoute>} />
          <Route path="/decisions" element={<ProtectedRoute><DecisionsPage /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/activity-tracker" element={<ProtectedRoute><ActivityTrackerPage /></ProtectedRoute>} />
          <Route path="/admissions-scoring" element={<ProtectedRoute><AdmissionsScoringPage /></ProtectedRoute>} />
          <Route path="/early-strategy" element={<ProtectedRoute><EarlyStrategyPage /></ProtectedRoute>} />
          <Route path="/mock-interview" element={<ProtectedRoute><MockInterviewPage /></ProtectedRoute>} />
          <Route path="/social-media-audit" element={<ProtectedRoute><SocialMediaAuditPage /></ProtectedRoute>} />
          <Route path="/rec-letters" element={<ProtectedRoute><RecLetterGuidePage /></ProtectedRoute>} />
          <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
          <Route path="/counselor" element={<ProtectedRoute><CounselorBotPage /></ProtectedRoute>} />
          <Route path="/discovery" element={<ProtectedRoute><DiscoveryAssessmentPage /></ProtectedRoute>} />
          <Route path="/application-review" element={<ProtectedRoute><ApplicationReviewPage /></ProtectedRoute>} />
          <Route path="/passion-projects" element={<ProtectedRoute><PassionProjectPage /></ProtectedRoute>} />
          <Route path="/visa-guidance" element={<ProtectedRoute><VisaGuidancePage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsDashboardPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CoachWidgetWrapper />
      <FeedbackWidget />
      <MobileTabBar />
      <CookieConsent />
    </BrowserRouter>
  );
}
